/**
 * Adapter matrix (principal / party / user vs UI row):
 * - `IPrincipalDisplay` / ExtendedAvatar: use after mapping from wire DTOs here.
 * - `Avatar` with firstName/lastName: keep when parent already holds IUserBrief — do not force IPrincipalDisplay.
 * - Principal column API (`IPrincipalColumn`): `principalColumnToDisplay`.
 * - Validator principal (`IValidatorPrincipal`): `validatorPrincipalToDisplay`.
 * - Dispute / involved party (`ISingleInvolvedPartie`): `involvedPartyToDisplay`.
 * - IUserBrief where a two-line row is needed: `userBriefToPrincipalDisplay`.
 * GlobalIndex / SignalR payloads: no OpenAPI contract; align row types with entities only after confirming backend shape.
 */

import { EProfileType, IUserBrief } from '../entities/IGlobal';
import {
    IPrincipalColumn,
    IPrincipalColumnInput,
    IPrincipalDisplay,
    IValidatorPrincipal,
    PrincipalColumnPrincipalTypeNumeric,
    PrincipalColumnPrincipalTypeWire,
    ValidatorPrincipalTypeNumeric
} from '../entities/IPrincipalColumn';
import { ISingleInvolvedPartie } from '../entities/IClusters';
import { normalizePrincipalType } from './principalType';

const empty = (v: string | null | undefined): string => v ?? '';

export function principalColumnNumericToEProfileType(
    n: PrincipalColumnPrincipalTypeWire | null | undefined
): EProfileType {
    if (n === 1 || n === '1') {
        return EProfileType.Group;
    }
    if (n === 2 || n === '2') {
        return EProfileType.LegalEntity;
    }
    if (n === 0 || n === '0') {
        return EProfileType.Personal;
    }
    if (typeof n === 'string') {
        const t = n.trim();
        if (!t) {
            return EProfileType.Personal;
        }
        const lower = t.toLowerCase();
        /** API often sends PascalCase enum names (e.g. `LegalEntity`); `normalizePrincipalType` only knows Group vs rest → LE became Personal. */
        if (lower === 'legalentity' || lower === 'company') {
            return EProfileType.LegalEntity;
        }
        if (lower === 'personal' || lower === 'user') {
            return EProfileType.Personal;
        }
        if (lower === 'group') {
            return EProfileType.Group;
        }
        if (lower === 'cluster') {
            return EProfileType.Cluster;
        }
        return normalizePrincipalType(t);
    }
    return EProfileType.Personal;
}

export function validatorPrincipalNumericToEProfileType(
    type: ValidatorPrincipalTypeNumeric | null | undefined
): EProfileType {
    return type === 1 ? EProfileType.Group : EProfileType.Personal;
}

export function principalColumnToDisplay(row: IPrincipalColumn): IPrincipalDisplay {
    return {
        childId: empty(row.childId),
        firstLine: empty(row.firstLine),
        secondLine: empty(row.secondLine),
        picture: empty(row.picture),
        principalType: principalColumnNumericToEProfileType(row.principalType ?? undefined)
    };
}

export function validatorPrincipalToDisplay(p: IValidatorPrincipal): IPrincipalDisplay {
    return {
        childId: empty(p.childId),
        firstLine: empty(p.childName),
        secondLine: empty(p.jobTitle),
        picture: empty(p.picture),
        principalType: validatorPrincipalNumericToEProfileType(p.type)
    };
}

/** Accepts API payloads where `type` may be numeric or legacy string (e.g. validation DTOs). */
export function validatorPrincipalPayloadToDisplay(p: {
    childId?: string | null;
    childName?: string | null;
    jobTitle?: string | null;
    picture?: string | null;
    type?: number | string | null;
}): IPrincipalDisplay {
    const numericType: ValidatorPrincipalTypeNumeric =
        p.type === 1 || p.type === 'Group' ? 1 : 0;
    return validatorPrincipalToDisplay({
        childId: p.childId,
        childName: p.childName,
        jobTitle: p.jobTitle,
        picture: p.picture,
        type: numericType
    });
}

/** Maps involved-party / dashboard / graph wire types to avatar profile type (square LE, redirect buttons). */
export function involvedPartyTypeToEProfileType(
    type: ISingleInvolvedPartie['type'] | ISingleInvolvedPartie['DataType'] | string | null | undefined
): EProfileType {
    if (type == null || type === '') {
        return EProfileType.Personal;
    }
    const lower = String(type).trim().toLowerCase();
    if (lower === 'cluster') {
        return EProfileType.Cluster;
    }
    if (lower === 'legalentity' || lower === 'company' || lower === 'organization') {
        return EProfileType.LegalEntity;
    }
    if (lower === 'personal' || lower === 'user') {
        return EProfileType.Personal;
    }
    if (lower === 'group') {
        return EProfileType.Group;
    }
    return normalizePrincipalType(type);
}

export function involvedPartyToDisplay(party: ISingleInvolvedPartie): IPrincipalDisplay {
    const name = (party.name || party.Name || '').trim();
    const subtitle = (party.jobTitle || party.subTitle || '').trim();
    return {
        childId: party.id,
        firstLine: name,
        secondLine: subtitle,
        picture: party.picture || party.Picture || '',
        principalType: involvedPartyTypeToEProfileType(party.type)
    };
}

export function userBriefToPrincipalDisplay(user: IUserBrief | null | undefined): IPrincipalDisplay {
    if (!user) {
        return {
            childId: '',
            firstLine: '',
            secondLine: '',
            picture: '',
            principalType: EProfileType.Personal
        };
    }
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    return {
        childId: user.id,
        firstLine: (user.fullName || `${first} ${last}`.trim() || first || last).trim(),
        secondLine: (user.jobTitle || '').trim(),
        picture: user.picture || '',
        principalType: user.type ?? EProfileType.Personal
    };
}

export function principalColumnInputToDisplay(input: IPrincipalColumnInput): IPrincipalDisplay {
    const nameFromParts = [input.firstName, input.lastName].filter(Boolean).join(' ').trim();
    const childId = empty(input.childId ?? input.id);
    const firstLine = empty(input.firstLine ?? input.fullName ?? nameFromParts);
    const typeRaw = input.principalType ?? input.type;
    const principalType = principalColumnNumericToEProfileType(typeRaw as PrincipalColumnPrincipalTypeWire);
    return {
        childId,
        firstLine,
        secondLine: '',
        picture: empty(input.picture),
        principalType
    };
}

export type IPartiesDatatableElement = IPrincipalColumn | ISingleInvolvedPartie;

function isPrincipalColumnRow(row: IPartiesDatatableElement): row is IPrincipalColumn {
    const r = row as IPrincipalColumn & Partial<ISingleInvolvedPartie>;
    if (r.childId) {
        return true;
    }
    if (r.principalType !== undefined && r.principalType !== null) {
        return true;
    }
    return Object.prototype.hasOwnProperty.call(row, 'firstLine');
}

export function datatablePartyRowToPickerShape(row: IPartiesDatatableElement): {
    id: string;
    name: string;
    picture: string;
    type: EProfileType;
} {
    if (isPrincipalColumnRow(row)) {
        const d = principalColumnToDisplay(row);
        return { id: d.childId, name: d.firstLine, picture: d.picture, type: d.principalType };
    }
    const d = involvedPartyToDisplay(row);
    return { id: d.childId, name: d.firstLine, picture: d.picture, type: d.principalType };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null;

/**
 * Narrow unknown JSON from inquiry convert endpoints to `IPrincipalColumn` when shape matches.
 */
function parseWirePrincipalType(pt: unknown): PrincipalColumnPrincipalTypeWire | undefined {
    if (pt === undefined || pt === null) {
        return undefined;
    }
    if (typeof pt === 'number' && (pt === 0 || pt === 1 || pt === 2)) {
        return pt as PrincipalColumnPrincipalTypeNumeric;
    }
    if (typeof pt === 'string') {
        return pt;
    }
    return undefined;
}

export function parsePrincipalColumnResponse(data: unknown): IPrincipalColumn | undefined {
    if (!isRecord(data)) {
        return undefined;
    }
    const principalType = parseWirePrincipalType(data.principalType);
    return {
        childId: typeof data.childId === 'string' ? data.childId : undefined,
        firstLine: typeof data.firstLine === 'string' ? data.firstLine : undefined,
        secondLine: typeof data.secondLine === 'string' ? data.secondLine : undefined,
        picture: typeof data.picture === 'string' ? data.picture : undefined,
        principalType
    };
}

/**
 * GlobalIndex manager/person fields moved from a Person list to a single principal.
 * Accepts a leftover array during BE rollout and returns the first element.
 */
export function toSinglePrincipal<T>(value: readonly T[] | null | undefined): T | undefined;
export function toSinglePrincipal<T>(value: T | null | undefined): T | undefined;
export function toSinglePrincipal<T>(value: T | readonly T[] | null | undefined): T | undefined;
export function toSinglePrincipal<T>(value: T | readonly T[] | null | undefined): T | undefined {
    if (value == null) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value[0] as T | undefined;
    }
    return value as T;
}

type PrincipalPresenceFields = {
    childId?: string | null;
    firstLine?: string | null;
    secondLine?: string | null;
    name?: string | null;
};

/**
 * GlobalIndex often sends a blank principal object instead of null when LE/manager is unset.
 * Empty objects are truthy and would still render an avatar pill.
 */
export function isPresentPrincipal<T extends PrincipalPresenceFields>(
    value: T | null | undefined
): value is T {
    return Boolean(value?.childId || value?.firstLine || value?.secondLine || value?.name);
}
